import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import t from '@zenlayer/zen-intl18';
import { useControllableValue } from 'ahooks';
import dayjs, { time2utc, utc2local, } from '@zenlayer/zen-design/esm/_util/day';
import { Checkbox, DatePicker, Space } from '@zenlayer/zen-design';
import { compareTimes, adjustDate } from './util';
import { dataListener_showUtc } from './index';
import { useState } from 'react';
import { RefreshButton } from '@zenlayer/portal-utils';
export const useRangePickerPeriods = (period = [
    'Today',
    '7Days',
    '15Days',
    'CurrentMonth',
    'LastMonth',
]) => {
    const showUTCValue = dataListener_showUtc.useValueListener();
    if (!period?.length) {
        return undefined;
    }
    const map = {
        Today: {
            name: t('GyHB9tBVyCRT'),
            value: [
                adjustDate({ showUTC: showUTCValue, trim: 'dayStart' }),
                adjustDate({ showUTC: showUTCValue, trim: 'dayEnd' }),
            ],
        },
        '7Days': {
            name: t('rb8c2ApYjCvd'),
            value: [
                adjustDate({
                    showUTC: showUTCValue,
                    subtract: { value: 7, unit: 'day' },
                    trim: 'dayStart',
                }),
                adjustDate({ showUTC: showUTCValue, trim: 'dayEnd' }),
            ],
        },
        '15Days': {
            name: t('y4ISlO-47VfL'),
            value: [
                adjustDate({
                    showUTC: showUTCValue,
                    subtract: { value: 15, unit: 'day' },
                    trim: 'dayStart',
                }),
                adjustDate({ showUTC: showUTCValue, trim: 'dayEnd' }),
            ],
        },
        CurrentMonth: {
            name: t('D6z3wBOT2eD3'),
            value: [
                adjustDate({
                    showUTC: showUTCValue,
                    trim: 'monthStart',
                }),
                adjustDate({ showUTC: showUTCValue, trim: 'dayEnd' }),
            ],
        },
        LastMonth: {
            name: t('my-jL9cfZ6Ul'),
            value: [
                adjustDate({
                    showUTC: showUTCValue,
                    subtract: { value: 1, unit: 'month' },
                    trim: 'monthStart',
                }),
                adjustDate({
                    showUTC: showUTCValue,
                    subtract: { value: 1, unit: 'month' },
                    trim: 'monthEnd',
                }),
            ],
        },
    };
    return period.reduce((p, r) => {
        return {
            ...p,
            [map[r].name]: map[r].value,
        };
    }, {});
};
export const useAdjustDefaultRangePickerValue = (data) => {
    const { defaultRangePickerValue, subtract, earliestTime, latestTime } = data;
    const showUTCValue = dataListener_showUtc.useValueListener();
    const [value, setValue] = useState(() => {
        if (defaultRangePickerValue) {
            return [
                adjustDate({
                    datetime: defaultRangePickerValue[0],
                    showUTC: showUTCValue,
                    trim: 'dayStart',
                    earliestTime,
                    returnString: true,
                }),
                adjustDate({
                    datetime: defaultRangePickerValue[1],
                    showUTC: showUTCValue,
                    trim: 'dayEnd',
                    latestTime,
                    returnString: true,
                }),
            ];
        }
        return [
            adjustDate({
                showUTC: showUTCValue,
                subtract: subtract,
                trim: 'dayStart',
                earliestTime,
                returnString: true,
            }),
            adjustDate({
                showUTC: showUTCValue,
                trim: 'dayEnd',
                latestTime,
                returnString: true,
            }),
        ];
    });
    return [value, setValue];
};
export function defaultDisabledDate(earliestTime, latestTime) {
    return (current) => {
        if (!current) {
            return false;
        }
        const earlyThanEarliest = !!earliestTime &&
            current.isBefore(adjustDate({ datetime: earliestTime, trim: 'dayStart' }));
        const lateThanLatest = latestTime === false
            ? false
            : current.isAfter(adjustDate({ datetime: latestTime, trim: 'dayEnd' }));
        return earlyThanEarliest || lateThanLatest;
    };
}
export const useDateUtils = () => {
    const showUTC = dataListener_showUtc.useValueListener();
    const _utc2local = (t, opts) => {
        return utc2local(t, {
            isUtcTimeZone: showUTC,
            ...opts,
        });
    };
    const _adjustDate = (opts) => {
        return adjustDate({
            showUTC,
            ...opts,
        });
    };
    return {
        utc2local: _utc2local,
        adjustDate: _adjustDate,
        time2utc,
    };
};
export const ChartDatePicker = (props) => {
    const { disabledDate, ranges, customRanges: _customRanges, datePicker_disabled, datePicker_getPopupContainer, earliestTime, latestTime, extra, onRefresh, datePickerClassName, datePickerStyle, } = props;
    const customRanges = useRangePickerPeriods(_customRanges);
    const showUTCValue = dataListener_showUtc.useValueListener();
    const offsetHours = dayjs().utcOffset() / 60;
    const utcStr = showUTCValue
        ? '+0'
        : `${offsetHours >= 0 ? '+' : '-'}${Math.abs(offsetHours)}`;
    const [rangePickerValue, onRangePickerChange] = useControllableValue(props, {
        defaultValue: undefined,
        defaultValuePropName: 'defaultRangePickerValue',
        valuePropName: 'rangePickerValue',
        trigger: 'onRangePickerChange',
    });
    const [isRefreshing, setIsRefreshing] = useState(false);
    return (_jsxs(Space, { align: "center", block: true, onClick: (e) => {
            e.stopPropagation();
        }, children: [props.rangePickerValue && (_jsx(DatePicker, { className: datePickerClassName, style: datePickerStyle, rangePicker: true, disabledDate: disabledDate || defaultDisabledDate(earliestTime, latestTime), ranges: ranges || customRanges, size: "s", value: [
                    adjustDate({
                        datetime: rangePickerValue?.[0],
                        showUTC: showUTCValue,
                    }),
                    adjustDate({
                        datetime: rangePickerValue?.[1],
                        showUTC: showUTCValue,
                    }),
                ], disabled: datePicker_disabled, getPopupContainer: datePicker_getPopupContainer, width: 380, onChange: (value) => {
                    if (value?.[0] && value?.[1]) {
                        const st = value[0].startOf('d');
                        const et = value[1].endOf('d');
                        onRangePickerChange?.([
                            time2utc(earliestTime ? compareTimes(dayjs(earliestTime), st)[1] : st),
                            time2utc(latestTime ? compareTimes(dayjs(latestTime), et)[0] : et),
                        ]);
                    }
                }, timeZone: t('saEyDRM1OVIY', {
                    offset: utcStr,
                }), renderExtraFooter: () => {
                    return (_jsx(Space, { block: true, children: _jsx(Checkbox, { block: true, checked: showUTCValue, onChange: (v) => {
                                dataListener_showUtc.setValue(v);
                            }, size: "s", children: t('Zk6psZu94Aoi') }) }));
                } })), extra, onRefresh && (_jsx(RefreshButton, { loading: isRefreshing, onClick: async () => {
                    setIsRefreshing(true);
                    await onRefresh(rangePickerValue);
                    setIsRefreshing(false);
                } }))] }));
};
